import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

const MODULO = "Resoluciones";

const normalizarTexto = (texto = "") =>
  texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const RegistrarResoluciones = () => {
  const { idEnte, idResolucion } = useParams();
  const navigate = useNavigate();

  const isEdit = useMemo(() => Boolean(idResolucion), [idResolucion]);

  const [loading, setLoading] = useState(false);

  const [codResolucionEnte, setCodResolucionEnte] = useState("");
  const [fechaResolucionEnte, setFechaResolucionEnte] = useState("");
  const [tipoResolucionEnte, setTipoResolucionEnte] = useState("");
  const [observacionResolucionEnte, setObservacionResolucionEnte] =
    useState("");

  const authConfig = () => {
    const token = localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const getModuloResoluciones = () => {
    try {
      const permisosStorage = localStorage.getItem("permisos");
      const usuarioStorage = localStorage.getItem("usuario");
      const userStorage = localStorage.getItem("user");
      const usuariosStorage = localStorage.getItem("usuarios");

      const permisosData = permisosStorage
        ? JSON.parse(permisosStorage)
        : usuarioStorage
        ? JSON.parse(usuarioStorage)
        : userStorage
        ? JSON.parse(userStorage)
        : usuariosStorage
        ? JSON.parse(usuariosStorage)
        : {};

      const modulos = Array.isArray(permisosData)
        ? permisosData
        : permisosData?.modulos ||
          permisosData?.permisos ||
          permisosData?.usuario?.modulos ||
          permisosData?.usuario?.permisos ||
          permisosData?.user?.modulos ||
          permisosData?.user?.permisos ||
          [];

      const moduloBuscado = normalizarTexto(MODULO);

      return (
        modulos.find((modulo) => {
          const nombre =
            modulo?.nombreModulo || modulo?.modulo || modulo?.nombre || "";

          return normalizarTexto(nombre) === moduloBuscado;
        }) || {}
      );
    } catch (error) {
      console.error("Error leyendo permisos:", error);
      return {};
    }
  };

  const moduloResoluciones = useMemo(() => getModuloResoluciones(), []);

  const puedeLeer =
    moduloResoluciones?.puedeLeer === true ||
    moduloResoluciones?.leer === true ||
    moduloResoluciones?.ver === true ||
    moduloResoluciones?.listar === true ||
    moduloResoluciones?.read === true ||
    moduloResoluciones?.view === true;

  const puedeCrear =
    moduloResoluciones?.puedeCrear === true ||
    moduloResoluciones?.crear === true ||
    moduloResoluciones?.create === true;

  const puedeActualizar =
    moduloResoluciones?.puedeActualizar === true ||
    moduloResoluciones?.actualizar === true ||
    moduloResoluciones?.editar === true ||
    moduloResoluciones?.update === true;

  const puedeGuardarFormulario = isEdit ? puedeActualizar : puedeCrear;

  const manejarErrorSesion = useCallback(
    (error) => {
      if (error?.response?.status === 401) {
        Swal.fire({
          icon: "warning",
          title: "Sesión expirada",
          text: "Por favor inicia sesión nuevamente.",
          confirmButtonText: "Aceptar",
        }).then(() => {
          localStorage.removeItem("token");
          navigate("/", { replace: true });
        });

        return true;
      }

      return false;
    },
    [navigate]
  );

  const cleanData = useCallback(() => {
    setCodResolucionEnte("");
    setFechaResolucionEnte("");
    setTipoResolucionEnte("");
    setObservacionResolucionEnte("");
  }, []);

  const getData = useCallback(async () => {
    if (isEdit && !puedeLeer) {
      cleanData();
      return;
    }

    try {
      setLoading(true);

      const { data } = await axios.get(
        `/api/resolucion/listid/${idResolucion}`,
        authConfig()
      );

      if (data?.ok) {
        const r = data.resolucionEnte;

        setCodResolucionEnte(
          r?.codResolucionEnte !== undefined && r?.codResolucionEnte !== null
            ? String(r.codResolucionEnte)
            : ""
        );

        setFechaResolucionEnte(r?.fechaResolucionEnte?.substring(0, 10) ?? "");
        setTipoResolucionEnte(r?.tipoResolucionEnte ?? "");
        setObservacionResolucionEnte(r?.observacionResolucionEnte ?? "");
      } else {
        Swal.fire("No encontrada", "No se encontró la resolución.", "warning");
      }
    } catch (error) {
      console.error("Error al obtener la resolución:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire("Error", "No se pudo cargar la resolución.", "error");
    } finally {
      setLoading(false);
    }
  }, [idResolucion, isEdit, puedeLeer, cleanData, manejarErrorSesion]);

  useEffect(() => {
    if (isEdit) getData();
  }, [isEdit, getData]);

  const saveResolucion = async () => {
    if (!puedeCrear) {
      Swal.fire(
        "Sin permisos",
        "No tiene permisos para registrar resoluciones.",
        "warning"
      );
      return;
    }

    try {
      if (!idEnte) {
        Swal.fire({
          icon: "error",
          title: "Falta el Ente",
          text: "No se recibió el id del EnteConformador en la URL.",
        });
        return;
      }

      if (!codResolucionEnte || Number.isNaN(Number(codResolucionEnte))) {
        Swal.fire({
          icon: "warning",
          title: "Validación",
          text: "El código de resolución debe ser un número válido.",
        });
        return;
      }

      setLoading(true);

      const newResolucion = {
        codResolucionEnte: Number(codResolucionEnte),
        fechaResolucionEnte,
        tipoResolucionEnte,
        observacionResolucionEnte,
        enteObjectId: idEnte,
      };

      await axios.post("/api/resolucion/add", newResolucion, authConfig());

      await Swal.fire({
        icon: "success",
        title: "Resolución registrada exitosamente",
        showConfirmButton: false,
        timer: 1500,
      });

      cleanData();
      navigate(-1);
    } catch (error) {
      console.error("Error en saveResolucion:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire({
        icon: "error",
        title: "Error al registrar la resolución",
        text:
          error.response?.data?.message ||
          "Ocurrió un error inesperado al guardar la resolución.",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateResolucion = async () => {
    if (!puedeActualizar) {
      Swal.fire(
        "Sin permisos",
        "No tiene permisos para actualizar resoluciones.",
        "warning"
      );
      return;
    }

    if (!puedeLeer) {
      Swal.fire(
        "Sin permisos",
        "No tiene permisos para consultar esta resolución.",
        "warning"
      );
      return;
    }

    try {
      if (!codResolucionEnte || Number.isNaN(Number(codResolucionEnte))) {
        Swal.fire({
          icon: "warning",
          title: "Validación",
          text: "El código de resolución debe ser un número válido.",
        });
        return;
      }

      setLoading(true);

      const updatedData = {
        codResolucionEnte: Number(codResolucionEnte),
        fechaResolucionEnte,
        tipoResolucionEnte,
        observacionResolucionEnte,
      };

      await axios.put(
        `/api/resolucion/update/${idResolucion}`,
        updatedData,
        authConfig()
      );

      await Swal.fire({
        icon: "success",
        title: "Resolución actualizada exitosamente",
        showConfirmButton: false,
        timer: 1500,
      });

      cleanData();
      navigate(-1);
    } catch (error) {
      console.error("Error al actualizar la resolución:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire({
        icon: "error",
        title: "Error al actualizar la resolución",
        text: error.response?.data?.message || "Ocurrió un error inesperado.",
      });
    } finally {
      setLoading(false);
    }
  };

  const actions = async (e) => {
    e.preventDefault();

    if (loading) return;

    isEdit ? await updateResolucion() : await saveResolucion();
  };

  const handleCancel = () => {
    cleanData();
    navigate(-1);
  };

  if (isEdit && !puedeLeer) {
    return (
      <div className="bg-body-tertiary py-4">
        <div className="container-fluid px-2 px-md-4">
          <div className="bg-white border rounded-4 shadow-sm p-4">
            <div className="text-center text-secondary py-5">
              <div className="display-6 mb-3">
                <i className="fa-solid fa-lock"></i>
              </div>

              <h5 className="fw-semibold text-dark">
                Sin permiso de lectura
              </h5>

              <p className="mb-4">
                No tienes permisos para consultar esta resolución.
              </p>

              <button
                className="btn btn-outline-dark"
                type="button"
                onClick={() => navigate(-1)}
              >
                <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                Volver
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isEdit && !puedeCrear) {
    return (
      <div className="bg-body-tertiary py-4">
        <div className="container-fluid px-2 px-md-4">
          <div className="bg-white border rounded-4 shadow-sm p-4">
            <div className="text-center text-secondary py-5">
              <div className="display-6 mb-3">
                <i className="fa-solid fa-lock"></i>
              </div>

              <h5 className="fw-semibold text-dark">
                Sin permiso de creación
              </h5>

              <p className="mb-4">
                No tienes permisos para registrar resoluciones.
              </p>

              <button
                className="btn btn-outline-dark"
                type="button"
                onClick={() => navigate(-1)}
              >
                <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                Volver
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isEdit && !puedeActualizar) {
    return (
      <div className="bg-body-tertiary py-4">
        <div className="container-fluid px-2 px-md-4">
          <div className="bg-white border rounded-4 shadow-sm p-4">
            <div className="text-center text-secondary py-5">
              <div className="display-6 mb-3">
                <i className="fa-solid fa-lock"></i>
              </div>

              <h5 className="fw-semibold text-dark">
                Sin permiso de actualización
              </h5>

              <p className="mb-4">
                No tienes permisos para editar resoluciones.
              </p>

              <button
                className="btn btn-outline-dark"
                type="button"
                onClick={() => navigate(-1)}
              >
                <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                Volver
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-body-tertiary py-4">
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  {isEdit ? "Editar Resolución" : "Registrar Resolución"}
                </h5>

                <small className="text-secondary">
                  Completa la información de la resolución asociada al ente.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-outline-dark"
                  onClick={() => navigate(-1)}
                  disabled={loading}
                  title="Volver"
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>
              </div>
            </div>
          </div>

          <div className="p-3 p-md-4">
            {loading && (
              <div className="text-center text-secondary py-3">
                <div className="spinner-border" role="status" />
                <div className="mt-2">Procesando…</div>
              </div>
            )}

            <form id="form-resolucion" onSubmit={actions} noValidate>
              <div className="row g-3">
                <div className="col-12 col-md-4">
                  <label className="form-label fw-semibold">
                    Código Resolución
                  </label>

                  <input
                    type="number"
                    className="form-control"
                    placeholder="Ej: 1001"
                    min="0"
                    value={codResolucionEnte}
                    onChange={(e) => setCodResolucionEnte(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>

                <div className="col-12 col-md-4">
                  <label className="form-label fw-semibold">
                    Fecha de Resolución
                  </label>

                  <input
                    type="date"
                    className="form-control"
                    value={fechaResolucionEnte}
                    onChange={(e) => setFechaResolucionEnte(e.target.value)}
                    disabled={loading}
                  />
                </div>

                <div className="col-12 col-md-4">
                  <label className="form-label fw-semibold">
                    Tipo de Resolución
                  </label>

                  <select
                    className="form-select"
                    value={tipoResolucionEnte}
                    onChange={(e) => setTipoResolucionEnte(e.target.value)}
                    disabled={loading}
                  >
                    <option value="">Seleccione…</option>
                    <option value="Tecnica">Técnica</option>
                    <option value="Academica">Académica</option>
                  </select>
                </div>

                <div className="col-12 col-md-12">
                  <label className="form-label fw-semibold">Observación</label>

                  <textarea
                    className="form-control"
                    placeholder="Escriba una observación (opcional)"
                    rows={5}
                    value={observacionResolucionEnte}
                    onChange={(e) =>
                      setObservacionResolucionEnte(e.target.value)
                    }
                    disabled={loading}
                  />
                </div>

                <div className="col-12 pt-2">
                  <div className="d-flex flex-wrap gap-2 justify-content-end">
                    <button
                      type="button"
                      className="btn btn-outline-dark"
                      onClick={cleanData}
                      disabled={loading}
                      title="Limpiar"
                    >
                      <i className="fa-solid fa-eraser me-2"></i>
                      Limpiar
                    </button>

                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={handleCancel}
                      disabled={loading}
                      title="Cancelar"
                    >
                      <i className="fa-solid fa-xmark me-2"></i>
                      Cancelar
                    </button>

                    {puedeGuardarFormulario && (
                      <button
                        type="submit"
                        form="form-resolucion"
                        className="btn btn-dark"
                        disabled={loading}
                        title={isEdit ? "Actualizar" : "Guardar"}
                      >
                        <i
                          className={`fa-solid ${
                            loading ? "fa-spinner fa-spin" : "fa-floppy-disk"
                          } me-2`}
                        ></i>

                        {loading
                          ? isEdit
                            ? "Actualizando…"
                            : "Guardando…"
                          : isEdit
                          ? "Actualizar"
                          : "Guardar"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </form>

            {!puedeGuardarFormulario && (
              <div className="alert alert-warning mt-3 mb-0">
                <i className="fa-solid fa-lock me-2"></i>
                No tienes permiso para {isEdit ? "actualizar" : "crear"}{" "}
                resoluciones.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrarResoluciones;